import { useCallback, useState } from 'react';
import {
  hasAcceptedLibrarySourceUrlRisk,
  setLibrarySourceUrlRiskAccepted,
} from '../../data';
import {
  useLibraryRiskOptinGate,
  type LibraryRiskOptinGate,
} from './useLibraryRiskOptinGate';

/**
 * Web-source (URL) risk opt-in gate shared by BOTH `WebSourcesStep` variants.
 *
 * Lives in its own module so the legacy wizard step (unified-collections OFF
 * "Sources" creation) and the unified modal content (flag ON) can't drift:
 * EVI-1762 was caused by only the unified `WebSourcesStepContent` wiring this
 * gate, so flag-off users could add web sources without ever acknowledging the
 * URL-source risks. Acceptance is localStorage-backed (`libraryUploadRisk`) and
 * the show/skip decision is delegated to {@link useLibraryRiskOptinGate} (its
 * own flag + accepted state).
 */
export const useLibrarySourceUrlRiskGate = (): LibraryRiskOptinGate => {
  // `useState` treats a function argument as a lazy initializer, so passing
  // `hasAcceptedLibrarySourceUrlRisk` (a zero-arg reader) correctly seeds from
  // localStorage without calling it at module-eval time.
  const [urlRiskAccepted, setUrlRiskAccepted] = useState(hasAcceptedLibrarySourceUrlRisk);
  const acceptUrlRisk = useCallback(() => {
    setLibrarySourceUrlRiskAccepted();
    setUrlRiskAccepted(true);
  }, []);
  return useLibraryRiskOptinGate({
    hasAccepted: urlRiskAccepted,
    accept: acceptUrlRisk,
  });
};
